import { sortStripeProductsByOrderNumber } from '@/common/stripe';
import { isDefined, safeSort } from '@/common/utils';
import { StandardButton } from '@/components/buttons/standard';
import { CustomContentSection } from '@/components/custom-content-section';
import { SubscriptionCard } from '@/components/subscription-card';
import { useLayoutContext } from '@/contexts';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { theme } from '@/theme';
import { Carousel } from '@mantine/carousel';
import { Flex, Grid } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import {
	StripeSubscriptionProductsQuery,
	useStripeSubscriptionProductsQuery,
} from './graphql.generated';
import styles from './styles.module.css';

export const ChooseSubscriptionPage = () => {
	const navigate = useNavigate();
	const { isDesktop } = useLayoutContext();
	const { loading, data, error } = useStripeSubscriptionProductsQuery();
	const products = safeSort(data?.stripeSubscriptionProducts, sortStripeProductsByOrderNumber);

	const goHome = useCallback(() => navigate({ to: '/home' }), [navigate]);
	return (
		<PrimaryLayout header={{ title: 'Subscription' }}>
			<Flex direction="column" gap="md" justify="space-between" align="center" flex={1} p="lg">
				<CustomContentSection
					location={ContentSectionLocation.ChooseSubscription}
					placement={ContentSectionPlacement.Top}
				/>
				<ContentWithLoad
					loading={loading}
					error={!!error}
					isEmpty={!products || products.length === 0}
					isEmptyMessage="No products available"
					isErrorMessage="An Error occurred"
				>
					{!isDesktop && (
						<Flex direction="column" justify="center" align="center" flex={1} w="100%">
							<Carousel withIndicators withControls height="100%" classNames={styles}>
								{products?.map((product) => (
									<Carousel.Slide key={product?.id}>
										<Product product={product} />
									</Carousel.Slide>
								))}
							</Carousel>
						</Flex>
					)}
					{isDesktop && (
						<Grid pt={100}>
							{products?.map((product) => (
								<Grid.Col key={product?.id} span={4}>
									<Product product={product} />
								</Grid.Col>
							))}
						</Grid>
					)}
				</ContentWithLoad>
				<CustomContentSection
					location={ContentSectionLocation.ChooseSubscription}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.ChooseSubscription}
					placement={ContentSectionPlacement.Bottom}
				/>
				<StandardButton variant="subtle" onClick={goHome}>
					Skip For Now
				</StandardButton>
			</Flex>
		</PrimaryLayout>
	);
};

const getInformationList = (metadataItem: string | null | undefined) => {
	if (typeof metadataItem !== 'string') {
		return undefined;
	}
	try {
		return JSON.parse(metadataItem);
	} catch {
		return undefined;
	}
};

type Product = NonNullable<StripeSubscriptionProductsQuery['stripeSubscriptionProducts']>[number];

const Product = ({ product }: { product: Product }) => {
	const navigate = useNavigate();
	const price = product?.default_price?.unit_amount;
	const perPeriod = 'year';
	const productName = product?.name;
	const subtitle1 = product?.metadata?.information_section_item_1;
	const subtitle2 = product?.metadata?.information_section_item_2;
	const bullets = getInformationList(product?.metadata?.information_section_list);
	const color = product?.metadata?.background_colour;
	const productId = product?.id;

	const goToSubscriptionDetail = useMemo(() => {
		if (!productId) {
			return null;
		}

		return (
			<StandardButton
				onClick={() => navigate({ to: `/subscription-detail/${productId}` })}
				variant="filled"
				key={productId}
				style={color ? { backgroundColor: color } : undefined}
			>
				Select {productName}
			</StandardButton>
		);
	}, [productId, productName, color, navigate]);

	if (!isDefined(price) || !isDefined(perPeriod) || !isDefined(productName)) {
		return null;
	}

	return (
		<SubscriptionCard
			color={color ?? theme.colors?.gray?.[1] ?? '#F5F5F5'}
			price={price}
			perPeriod={perPeriod}
			name={productName}
			subtitle1={subtitle1}
			subtitle2={subtitle2}
			bullets={bullets}
			buttons={[goToSubscriptionDetail]}
		/>
	);
};
